// React Query hooks for CardRadar API
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/cr";

export function useReference() {
  return useQuery({
    queryKey: ["reference"],
    queryFn: async () => (await api.get("/reference")).data,
    staleTime: 5 * 60_000,
  });
}

export function useListings(filters) {
  return useQuery({
    queryKey: ["listings", filters],
    queryFn: async () => {
      const params = {};
      Object.entries(filters || {}).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined) params[k] = v;
      });
      return (await api.get("/listings", { params })).data;
    },
    refetchInterval: 8000,
  });
}

export function useStats() {
  return useQuery({
    queryKey: ["stats"],
    queryFn: async () => (await api.get("/stats")).data,
    refetchInterval: 6000,
  });
}

export function useAlerts(limit = 60) {
  return useQuery({
    queryKey: ["alerts", limit],
    queryFn: async () => (await api.get("/alerts", { params: { limit } })).data,
    refetchInterval: 4000,
  });
}

export function useWatchlist() {
  return useQuery({
    queryKey: ["watchlist"],
    queryFn: async () => (await api.get("/watchlist")).data,
  });
}

export function useCreateWatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => (await api.post("/watchlist", body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["watchlist"] }),
  });
}

export function useDeleteWatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.delete(`/watchlist/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["watchlist"] }),
  });
}

export function useScannerState() {
  return useQuery({
    queryKey: ["scanner"],
    queryFn: async () => (await api.get("/scanner")).data,
    refetchInterval: 3000,
  });
}

export function useStartScanner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (interval = 20) => (await api.post("/scanner/start", null, { params: { interval } })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["scanner"] }),
  });
}

export function useStopScanner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.post("/scanner/stop")).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["scanner"] }),
  });
}

export function useRunScanOnce() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.post("/scanner/run-once")).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["listings"] });
      qc.invalidateQueries({ queryKey: ["alerts"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
    },
  });
}

export function useRestartScanner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.post("/scanner/restart")).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["scanner"] });
      qc.invalidateQueries({ queryKey: ["scanner-activity"] });
    },
  });
}

export function useWebhooks() {
  return useQuery({
    queryKey: ["webhooks"],
    queryFn: async () => (await api.get("/webhooks")).data,
  });
}

export function useUpdateWebhooks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => (await api.put("/webhooks", body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["webhooks"] }),
  });
}

export function useTestWebhook() {
  return useMutation({
    mutationFn: async (target) => (await api.post("/webhooks/test", null, { params: { target } })).data,
  });
}

export function useMarkAlertsSeen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.post("/alerts/mark-seen")).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts"] }),
  });
}

export function useClearAlerts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.delete("/alerts")).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts"] }),
  });
}

export function useDesktopInfo() {
  return useQuery({
    queryKey: ["desktop-info"],
    queryFn: async () => (await api.get("/desktop/info")).data,
  });
}

// ---- Accounts ----
export function useAccounts() {
  return useQuery({
    queryKey: ["accounts"],
    queryFn: async () => (await api.get("/accounts")).data,
  });
}

export function useCreateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => (await api.post("/accounts", body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["accounts"] }),
  });
}
export function useUpdateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) => (await api.patch(`/accounts/${id}`, body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["accounts"] }),
  });
}

export function useDeleteAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.delete(`/accounts/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["accounts"] }),
  });
}

export function useTestAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.post(`/accounts/${id}/test`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["accounts"] }),
  });
}

// ---- Payments ----
export function usePayments() {
  return useQuery({
    queryKey: ["payments"],
    queryFn: async () => (await api.get("/payments")).data,
  });
}

export function useCreatePayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => (await api.post("/payments", body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payments"] }),
  });
}

export function useDeletePayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.delete(`/payments/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payments"] }),
  });
}

export function useUpdatePayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) => (await api.patch(`/payments/${id}`, body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payments"] }),
  });
}

export function useSetDefaultPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.post(`/payments/${id}/default`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payments"] }),
  });
}

// ---- Shipping profiles (address book for guest checkout) ----
export function useShippingProfiles() {
  return useQuery({
    queryKey: ["shipping-profiles"],
    queryFn: async () => (await api.get("/shipping-profiles")).data,
  });
}

export function useCreateShippingProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => (await api.post("/shipping-profiles", body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shipping-profiles"] }),
  });
}

export function useDeleteShippingProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.delete(`/shipping-profiles/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shipping-profiles"] }),
  });
}

export function useUpdateShippingProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) => (await api.patch(`/shipping-profiles/${id}`, body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shipping-profiles"] }),
  });
}

export function useSetDefaultShippingProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.post(`/shipping-profiles/${id}/default`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shipping-profiles"] }),
  });
}

// ---- Scalp profiles + attempts ----
export function useScalpProfiles() {
  return useQuery({
    queryKey: ["scalp-profiles"],
    queryFn: async () => (await api.get("/scalp/profiles")).data,
    refetchInterval: 5000,
  });
}

export function useCreateScalpProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => (await api.post("/scalp/profiles", body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["scalp-profiles"] }),
  });
}

export function useUpdateScalpProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) => (await api.patch(`/scalp/profiles/${id}`, body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["scalp-profiles"] }),
  });
}

export function useDeleteScalpProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.delete(`/scalp/profiles/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["scalp-profiles"] }),
  });
}

export function useScalpAttempts(limit = 60) {
  return useQuery({
    queryKey: ["scalp-attempts", limit],
    queryFn: async () => (await api.get("/scalp/attempts", { params: { limit } })).data,
    refetchInterval: 2000,
  });
}

export function useClearAttempts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.delete("/scalp/attempts")).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["scalp-attempts"] }),
  });
}

export function useTestFireScalp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.post(`/scalp/test-fire/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["scalp-attempts"] });
      qc.invalidateQueries({ queryKey: ["scalp-profiles"] });
    },
  });
}

// ---- Proxies ----
export function useProxies() {
  return useQuery({
    queryKey: ["proxies"],
    queryFn: async () => (await api.get("/proxies")).data,
    retry: false,
  });
}

export function useCreateProxy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => (await api.post("/proxies", body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["proxies"] }),
  });
}

export function useDeleteProxy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.delete(`/proxies/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["proxies"] }),
  });
}

export function useUpdateProxy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) => (await api.patch(`/proxies/${id}`, body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["proxies"] }),
  });
}

export function useTestProxy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.post(`/proxies/${id}/test`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["proxies"] }),
  });
}

export function useBulkImportProxies() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => (await api.post("/proxies/bulk", body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["proxies"] }),
  });
}

// ---- Captcha ----
export function useCaptchaConfig() {
  return useQuery({
    queryKey: ["captcha-config"],
    queryFn: async () => (await api.get("/captcha/config")).data,
    retry: false,
  });
}

export function useUpdateCaptchaConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => (await api.put("/captcha/config", body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["captcha-config"] }),
  });
}

export function useCaptchaBalance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.post("/captcha/balance")).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["captcha-config"] }),
  });
}

// ---- Scraping Provider (Bright Data Scraping Browser) ----
export function useScrapingProvider() {
  return useQuery({
    queryKey: ["scraping-provider"],
    queryFn: async () => (await api.get("/desktop/scraping-provider")).data,
    retry: false,
  });
}

export function useUpdateScrapingProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => (await api.post("/desktop/scraping-provider", body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["scraping-provider"] }),
  });
}

export function useTestScrapingProvider() {
  return useMutation({
    mutationFn: async () => (await api.post("/desktop/scraping-provider/test")).data,
  });
}

// ---- Bright Data Web Scraper IDE (Scraper Studio) collectors ----
export function useBDAdminKeyStatus(enabled = true) {
  return useQuery({
    queryKey: ["bd-admin-key"],
    queryFn: async () => (await api.get("/desktop/bd/admin-key-status")).data,
    retry: false,
    enabled,
  });
}
export function useSaveBDAdminKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => (await api.post("/desktop/bd/admin-key", body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["bd-admin-key"] }),
  });
}
export function useBDUserKeyStatus() {
  return useQuery({
    queryKey: ["bd-user-key"],
    queryFn: async () => (await api.get("/desktop/bd/user-key-status")).data,
    retry: false,
  });
}
export function useSaveBDUserKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => (await api.post("/desktop/bd/user-key", body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["bd-user-key"] }),
  });
}
export function useBDCollectors() {
  return useQuery({
    queryKey: ["bd-collectors"],
    queryFn: async () => (await api.get("/desktop/bd/collectors")).data,
    retry: false,
  });
}
export function useCreateBDCollector() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ scope, body }) =>
      (await api.post(`/desktop/bd/collectors?scope=${scope}`, body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["bd-collectors"] }),
  });
}
export function useUpdateBDCollector() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) =>
      (await api.patch(`/desktop/bd/collectors/${id}`, body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["bd-collectors"] }),
  });
}
export function useDeleteBDCollector() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.delete(`/desktop/bd/collectors/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["bd-collectors"] }),
  });
}
export function useTestBDCollector() {
  return useMutation({
    mutationFn: async (id) => (await api.post(`/desktop/bd/collectors/${id}/test`)).data,
  });
}
export function useDiscoverBDCollectors() {
  return useMutation({
    mutationFn: async (scope) => (await api.get(`/desktop/bd/discover?scope=${scope}`)).data,
  });
}

// ---- BD budget / usage ----
export function useBDBudget() {
  return useQuery({
    queryKey: ["bd-budget"],
    queryFn: async () => (await api.get("/desktop/bd/budget")).data,
    refetchInterval: 30000,
    retry: false,
  });
}
export function useSaveBDAdminBudget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => (await api.post("/desktop/bd/admin-budget", body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["bd-budget"] }),
  });
}
export function useSaveBDUserBudget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => (await api.post("/desktop/bd/user-budget", body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["bd-budget"] }),
  });
}
export function useResetBDBudget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (scope) => (await api.post(`/desktop/bd/budget-reset?scope=${scope}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["bd-budget"] }),
  });
}
export function useRunNowBDCollector() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.post(`/desktop/bd/collectors/${id}/run-now`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["bd-collectors"] }),
  });
}

// ---- Scalp browser window management ----
export function useOpenScalpBrowsers() {
  return useQuery({
    queryKey: ["open-scalp-browsers"],
    queryFn: async () => (await api.get("/desktop/scalp/open-browsers")).data,
    refetchInterval: 5000,
    retry: false,
  });
}
export function useCloseAllScalpBrowsers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.post("/desktop/scalp/close-all-browsers")).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["open-scalp-browsers"] }),
  });
}

// ---- BD mode (BYO vs hybrid) ----
export function useBDMode() {
  return useQuery({
    queryKey: ["bd-mode"],
    queryFn: async () => (await api.get("/desktop/bd/mode")).data,
    retry: false,
  });
}
export function useSwitchBDMode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => (await api.post("/desktop/bd/mode", body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["bd-mode"] });
      qc.invalidateQueries({ queryKey: ["bd-budget"] });
      qc.invalidateQueries({ queryKey: ["bd-collectors"] });
    },
  });
}

// ---- Captcha resolve / cancel attempt ----
export function useResolveCaptcha() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.post(`/scalp/attempts/${id}/resolve-captcha`, { token: null })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["scalp-attempts"] }),
  });
}

export function useCancelAttempt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.post(`/scalp/attempts/${id}/cancel`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["scalp-attempts"] }),
  });
}

// ---- Adapters ----
export function useAdapters() {
  return useQuery({
    queryKey: ["adapters"],
    queryFn: async () => (await api.get("/adapters")).data,
  });
}

// ---- Vault ----
export function useVaultStatus() {
  return useQuery({
    queryKey: ["vault-status"],
    queryFn: async () => (await api.get("/vault/status")).data,
    refetchInterval: 30_000,
  });
}

export function useVaultSetup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (password) => (await api.post("/vault/setup", { password })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["vault-status"] }),
  });
}

export function useVaultUnlock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (password) => (await api.post("/vault/unlock", { password })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["vault-status"] }),
  });
}

export function useVaultLock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.post("/vault/lock")).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["vault-status"] }),
  });
}

// ---- License ----
export function useLicense() {
  return useQuery({
    queryKey: ["license"],
    queryFn: async () => (await api.get("/license/validate")).data,
    refetchInterval: 60_000,
    retry: false,
  });
}
